import type { ChannelEvent } from '../api/types'
import { db } from '../db/db'
import { useSession } from '../state/session'
import type { SoundName } from './clips'
import { soundPlayer } from './player'
import { soundForDeviceRequest, soundForMessage, soundForSent, vibrationFor } from './rules'
import { useSoundSettings } from './settings'

let lastPlayedAt = 0

const isVisible = () => typeof document !== 'undefined' && document.visibilityState === 'visible'

async function play(sound: SoundName | null): Promise<void> {
  if (!sound) return
  const settings = useSoundSettings.getState()
  lastPlayedAt = Date.now()
  const played = await soundPlayer.play(sound, settings.volume)
  const pattern = vibrationFor(sound, settings)
  if (played && pattern && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate(pattern)
    } catch {
      // Some browsers throw when vibration is blocked; the sound already played.
    }
  }
}

/** Call for message.created events that arrived live over the hub and were applied locally. */
export async function soundForLiveEvent(event: ChannelEvent): Promise<void> {
  if (event.type !== 'message.created') return
  const me = useSession.getState().me
  if (!me) return
  const channel = await db.channels.get(event.channelId)
  const message = event.payload.message
  await play(
    soundForMessage({
      settings: useSoundSettings.getState(),
      visible: isVisible(),
      now: Date.now(),
      lastPlayedAt,
      me: { id: me.id, username: me.username },
      message: { authorId: message.authorId, body: message.body, deleted: Boolean(message.deletedAt), forwarded: Boolean(message.forwarded) },
      channel: channel && { id: channel.id, type: channel.type, notify: channel.notify, mutedUntil: channel.mutedUntil, isMember: channel.isMember },
    }),
  )
}

export function soundForDeviceRequested(): Promise<void> {
  return play(
    soundForDeviceRequest({
      settings: useSoundSettings.getState(),
      visible: isVisible(),
      now: Date.now(),
      lastPlayedAt,
      isSysadmin: useSession.getState().me?.role === 'sysadmin',
    }),
  )
}

export function soundForMessageSent(): Promise<void> {
  return play(soundForSent({ settings: useSoundSettings.getState(), visible: isVisible(), now: Date.now(), lastPlayedAt }))
}

/** Settings screen "Play" buttons: also unlocks audio, since it runs inside a click. */
export async function previewSound(sound: SoundName): Promise<boolean> {
  await soundPlayer.unlock()
  return soundPlayer.play(sound, useSoundSettings.getState().volume)
}
